/**
 * Marca do cliente aplicada ao painel.
 *
 * A tela de Marca gravava `accent` e `logo_url` na organização, e nada no
 * aplicativo lia esses campos: salvava e a interface continuava amarela. Aqui a
 * cor escolhida vira os sete tokens que o CSS já usa, de uma vez, para não
 * existir um botão com a cor nova ao lado de uma aba com a antiga.
 *
 * Só a cor de destaque muda. Fundo, texto e superfície continuam do tema:
 * deixar o cliente pintar tudo é o caminho conhecido para telas ilegíveis.
 */

/**
 * V35: qual logo mostrar, dado o fundo em que ele vai aparecer.
 *
 * `logo_dark` é desenhado para ir sobre fundo ESCURO (texto claro sobre
 * grafite) - usá-lo num fundo claro (ou o contrário) não é só feio, é
 * ilegível. Antes a porta (`Auth.tsx`) nunca olhava para `logo_dark`
 * (o painel esquerdo dela é sempre grafite, por desenho - exatamente o
 * fundo para o qual esse logo existe) e o `Shell` caía nele mesmo no tema
 * claro quando `logo_url` estava vazio. Sem o par certo para o tema, não
 * mostrar logo nenhum é melhor que mostrar o errado.
 */
export function logoDaMarca(
  marca: { logo_url?: string | null; logo_dark?: string | null; icon_url?: string | null } | null | undefined,
  tema: 'light' | 'dark',
): string {
  if (!marca) return '';
  if (tema === 'dark') return marca.logo_dark || marca.logo_url || marca.icon_url || '';
  return marca.logo_url || marca.icon_url || '';
}

const TOKENS = [
  '--accent',
  '--accent-alt',
  '--accent-hover',
  '--accent-press',
  '--accent-text',
  '--on-accent',
  '--accent-tint',
  '--accent-border',
] as const;

type Rgb = { r: number; g: number; b: number };

/** Aceita #rgb e #rrggbb. Qualquer outra coisa devolve nulo e a marca é ignorada. */
function lerHex(valor: string): Rgb | null {
  const v = valor.trim().replace(/^#/, '');
  const hex = v.length === 3 ? v.replace(/(.)/g, '$1$1') : v;
  if (!/^[0-9a-f]{6}$/i.test(hex)) return null;
  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16),
  };
}

const limite = (n: number) => Math.max(0, Math.min(255, Math.round(n)));

const paraHex = ({ r, g, b }: Rgb) =>
  '#' + [r, g, b].map((n) => limite(n).toString(16).padStart(2, '0')).join('');

/** Fator acima de 1 clareia, abaixo escurece. */
const ajustar = (c: Rgb, fator: number): Rgb => ({
  r: fator >= 1 ? c.r + (255 - c.r) * (fator - 1) : c.r * fator,
  g: fator >= 1 ? c.g + (255 - c.g) * (fator - 1) : c.g * fator,
  b: fator >= 1 ? c.b + (255 - c.b) * (fator - 1) : c.b * fator,
});

/** Luminância relativa (WCAG). Decide se o texto sobre a cor é claro ou escuro. */
function luminancia({ r, g, b }: Rgb): number {
  const canal = (n: number) => {
    const s = n / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * canal(r) + 0.7152 * canal(g) + 0.0722 * canal(b);
}

// V32: o ponto de virada WCAG entre "preto lê melhor" e "branco lê melhor"
// sobre um fundo de luminância L não é 0,5 - é onde o contraste contra preto
// (`(L+0,05)/0,05`) e contra branco (`1,05/(L+0,05)`) empatam:
//   1,05/(L+0,05) = (L+0,05)/0,05  =>  (L+0,05)² = 0,0525  =>  L ≈ 0,179
// Abaixo de 0,5 e acima de ~0,179 (uma faixa larga - a maioria das cores de
// marca médias cai nela) o texto branco ficava escolhido quando o preto já
// dava mais contraste.
const LIMIAR_TEXTO_CLARO = 0.179;

/**
 * Os tokens derivados de uma cor, como objeto. Serve para dois usos: pintar o
 * documento inteiro (aplicarMarca) e pintar só um pedaço, como a prévia da tela
 * de Marca, onde o resultado precisa aparecer antes de salvar.
 *
 * `tema` é o fundo real que o `--accent-text` vai encontrar - claro por
 * padrão, para quem chama sem saber (V29: antes só o lado claro era
 * tratado; uma cor de marca escura - grafite, vinho, oceano - virava texto
 * ilegível sobre o `--bg` escuro do tema noturno, 1,2-1,9:1 de contraste
 * onde a WCAG pede 4,5:1).
 */
export function tokensDaCor(accent: string, tema: 'light' | 'dark' = 'light'): Record<string, string> {
  const cor = lerHex(accent);
  if (!cor) return {};

  const lum = luminancia(cor);
  const { r, g, b } = cor;

  // Texto escrito NA cor precisa de contraste contra o fundo da PÁGINA, não
  // contra a própria cor: no tema claro (fundo claro), cor clara demais vira
  // ilegível; no tema escuro (fundo escuro), é a cor ESCURA demais que some.
  const textoDaCor =
    tema === 'dark'
      ? lum < 0.45
        ? ajustar(cor, 1.8)
        : cor
      : lum > 0.45
        ? ajustar(cor, 0.65)
        : cor;

  return {
    '--accent': paraHex(cor),
    '--accent-hover': paraHex(ajustar(cor, lum > 0.5 ? 0.92 : 1.12)),
    '--accent-press': paraHex(ajustar(cor, lum > 0.5 ? 0.84 : 0.9)),
    '--accent-text': paraHex(textoDaCor),
    '--on-accent': lum > LIMIAR_TEXTO_CLARO ? '#101010' : '#ffffff',
    '--accent-tint': `rgba(${r},${g},${b},.12)`,
    '--accent-border': `rgba(${r},${g},${b},.30)`,
  };
}

/**
 * Aplica a marca no documento. Sem cor, devolve os tokens ao valor do tema, que
 * é o que acontece quando o cliente limpa o campo.
 */
/** Cantos por escala, não por valor solto: a proporção entre eles é o desenho. */
export const FORMAS: Record<string, Record<string, string>> = {
  reto: { '--radius-sm': '2px', '--radius-md': '3px', '--radius-lg': '4px', '--radius-xl': '6px' },
  padrao: {},
  suave: { '--radius-sm': '10px', '--radius-md': '14px', '--radius-lg': '20px', '--radius-xl': '26px' },
};

const TOKENS_FORMA = ['--radius-sm', '--radius-md', '--radius-lg', '--radius-xl'];

export function aplicarMarca(
  accent?: string | null,
  accentAlt?: string | null,
  forma?: string | null,
  // V29: o tema em que a marca está sendo pintada - decide para que lado
  // `--accent-text` precisa puxar o contraste. Quem chama sem saber o tema
  // (a prévia da tela de Marca, por exemplo) recebe o padrão claro.
  tema: 'light' | 'dark' = 'light',
): void {
  const raiz = document.documentElement;
  for (const t of TOKENS) raiz.style.removeProperty(t);
  for (const t of TOKENS_FORMA) {
    raiz.style.removeProperty(t);
    raiz.style.removeProperty(t.replace('--radius-', '--suite-client-radius-'));
  }

  for (const [token, valor] of Object.entries(FORMAS[forma ?? 'padrao'] ?? {})) {
    raiz.style.setProperty(token, valor);
    raiz.style.setProperty(token.replace('--radius-', '--suite-client-radius-'), valor);
  }

  for (const [token, valor] of Object.entries(accent ? tokensDaCor(accent, tema) : {})) {
    raiz.style.setProperty(token, valor);
  }

  const alt = accentAlt ? lerHex(accentAlt) : null;
  if (alt) raiz.style.setProperty('--accent-alt', paraHex(alt));
}
